(() => {
  if (customElements.get('premium-wishlist-button')) return;
  const messages = JSON.parse(document.getElementById('PremiumShoppingStrings')?.textContent || '{}');
  const memory = new Map();
  let storageFailed = false;
  let announcementTimer;
  const valid = value => typeof value === 'string' && value.length <= 200 && !/[\s/?#\\<>&]/.test(value);
  const read = kind => {
    if (storageFailed) return memory.get(kind) || [];
    try {
      const stored = JSON.parse(localStorage.getItem('premium.' + kind) || '[]');
      return Array.isArray(stored) ? [...new Set(stored.filter(valid))].slice(0, 48) : [];
    } catch (error) {
      storageFailed = true;
      console.warn('[Premium saved products] Storage unavailable.', error);
      return memory.get(kind) || [];
    }
  };
  const announce = text => {
    const status = document.getElementById('PremiumShoppingStatus');
    if (status) { status.hidden = false; status.textContent = text; clearTimeout(announcementTimer); announcementTimer = setTimeout(() => { status.hidden = true; }, 4500); }
  };
  const write = (kind, handles) => {
    memory.set(kind, handles);
    let persistent = true;
    try { localStorage.setItem('premium.' + kind, JSON.stringify(handles)); }
    catch (error) { persistent = false; storageFailed = true; console.warn('[Premium saved products] Using memory for this visit.', error); }
    document.dispatchEvent(new CustomEvent('premium:' + kind + ':change', { detail: { handles, persistent } }));
    return persistent;
  };
  const cache = new Map();
  class WishlistButton extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.button = this.querySelector('button');
      if (!this.button || !valid(this.dataset.handle)) return;
      this.button.hidden = false;
      this.sync = () => {
        const saved = read('wishlist').includes(this.dataset.handle);
        this.button.setAttribute('aria-pressed', String(saved));
        this.button.setAttribute('aria-label', saved ? this.button.dataset.removeLabel : this.button.dataset.saveLabel);
      };
      this.button.addEventListener('click', () => {
        if (!this.dispatchEvent(new CustomEvent('premium:wishlist:toggle', { bubbles: true, cancelable: true, detail: { handle: this.dataset.handle } }))) return;
        const handles = read('wishlist');
        const saved = handles.includes(this.dataset.handle);
        const persistent = write('wishlist', saved ? handles.filter(h => h !== this.dataset.handle) : [this.dataset.handle, ...handles].slice(0,48));
        announce(persistent ? (saved ? messages.removed : messages.saved) : messages.storage);
      }, { signal: this.controller.signal });
      document.addEventListener('premium:wishlist:change', this.sync, { signal: this.controller.signal });
      window.addEventListener('storage', this.sync, { signal: this.controller.signal });
      this.sync();
    }
    disconnectedCallback() { this.controller?.abort(); }
  }
  class ProductHistory extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.shown = 8;
      this.grid = this.querySelector('[data-history-grid]');
      this.status = this.querySelector('[data-history-status]');
      this.more = this.querySelector('[data-history-more]');
      this.more?.addEventListener('click', () => { this.shown += 8; this.load(); }, { signal: this.controller.signal });
      document.addEventListener('premium:' + this.dataset.kind + ':change', () => {
        if (this.loaded) this.load();
      }, { signal: this.controller.signal });
      window.addEventListener('storage', () => { if (this.loaded) this.load(); }, { signal: this.controller.signal });
      this.observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) { this.observer.disconnect(); this.load(); }
      });
      this.observer.observe(this);
    }
    async load() {
      this.loaded = true;
      this.request?.abort();
      this.request = new AbortController();
      const { signal } = this.request;
      const handles = read(this.dataset.kind).filter(h => h !== this.dataset.exclude).slice(0,Number(this.dataset.limit) || 8);
      const visible = handles.slice(0,this.shown);
      if (!visible.length) {
        this.removeAttribute('aria-busy');
        this.grid.replaceChildren();
        this.status.textContent = messages.empty;
        if (this.more) this.more.hidden = true;
        return;
      }
      this.setAttribute('aria-busy','true');
      const restoreFocus = this.contains(document.activeElement);
      const results = [];
      let failed = false;
      for (let offset = 0; offset < visible.length; offset += 4) {
        const batch = await Promise.all(visible.slice(offset,offset+4).map(async handle => {
          try {
            const url = new URL(this.dataset.root.replace(/\/?$/, '/') + 'products/' + encodeURIComponent(handle), location.origin);
            url.searchParams.set('section_id','premium-product-fragment');
            let html = cache.get(url.href);
            if (!html) {
              const response = await fetch(url, { signal, headers: { Accept: 'text/html' } });
              if (!response.ok) throw new Error('Product response ' + response.status);
              html = await response.text();
              cache.set(url.href, html);
            }
            const card = new DOMParser().parseFromString(html,'text/html').querySelector('product-component, .product-card-wrapper');
            if (!card) throw new Error('Product is no longer available');
            const prefix = this.id + '-' + handle + '-';
            card.querySelectorAll('[id]').forEach(element => { element.id = prefix + element.id; });
            for (const attribute of ['for','aria-labelledby','aria-describedby','aria-controls','form']) {
              card.querySelectorAll('['+attribute+']').forEach(element => {
                element.setAttribute(attribute, element.getAttribute(attribute).split(' ').map(id=>prefix+id).join(' '));
              });
            }
            return card;
          } catch (error) {
            if (error.name === 'AbortError') return null;
            failed = true;
            console.warn('[Premium saved products] Unable to load product.', error);
            return null;
          }
        }));
        if (signal.aborted) return;
        results.push(...batch.filter(Boolean));
      }
      this.grid.replaceChildren(...results);
      if (restoreFocus) (this.grid.querySelector('button:not([hidden]), a') || this.closest('dialog')?.querySelector('[data-wishlist-close]'))?.focus();
      this.status.textContent = failed ? messages.error : '';
      if (this.more) this.more.hidden = visible.length >= handles.length;
      this.removeAttribute('aria-busy');
    }
    disconnectedCallback() {
      this.controller?.abort(); this.request?.abort(); this.observer?.disconnect();
    }
  }
  class WishlistDialog extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.dialog = this.querySelector('dialog');
      document.addEventListener('click', event => {
        const trigger = event.target.closest('[data-wishlist-open]');
        if (!trigger) return;
        event.preventDefault();
        const drawer = trigger.closest('header-drawer');
        this.opener = drawer?.querySelector('summary') || trigger;
        if (drawer) drawer.closeMenuDrawer(event, this.opener);
        if (!this.dialog.open) this.dialog.showModal();
        this.querySelector('premium-product-history')?.load();
      }, { signal: this.controller.signal });
      this.querySelector('[data-wishlist-close]').addEventListener('click', () => this.dialog.close(), { signal: this.controller.signal });
      this.dialog.addEventListener('close', () => this.opener?.focus(), { signal: this.controller.signal });
      this.dialog.addEventListener('click', event => {
        if (event.target === this.dialog) {
          const box = this.dialog.getBoundingClientRect();
          if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.dialog.close();
        }
      }, { signal: this.controller.signal });
    }
    disconnectedCallback() { this.dialog?.close(); this.controller?.abort(); }
  }
  customElements.define('premium-wishlist-button', WishlistButton);
  customElements.define('premium-product-history', ProductHistory);
  customElements.define('premium-wishlist-dialog', WishlistDialog);
  const record = event => {
    const candidate = event?.target?.matches?.('product-info[data-update-url="true"]') ? event.target : null;
    const product = candidate || document.querySelector('product-info[data-premium-handle][data-update-url="true"]');
    const handle = product?.dataset.premiumHandle;
    if (valid(handle)) write('recent', [handle, ...read('recent').filter(h => h !== handle)].slice(0,24));
  };
  record();
  document.addEventListener('shopify:section:load', record);
  document.addEventListener('product-info:loaded', record);
})();